import { useMemo, useState } from 'react';
import { Link, useMatch } from '@tanstack/react-router';
import { ChevronRight, Search, X } from 'lucide-react';
import { useEndpointsByTag } from '@/api/hooks';
import { tagSlug, endpointSlug } from '@/lib/slug';
import { usePanels, setSidebarOpen } from '@/store/uiPanels';
import { cn } from '@/lib/cn';
import { MethodBadge } from './MethodBadge';
import type { EndpointInfo } from '@/api/client';

/**
 * Endpoint browser sidebar.
 *
 * - Collapsible tag groups (one per route file, e.g. "Chats", "Auth")
 * - Active-route highlighting via TanStack Router's useMatch
 * - Filter input narrows by path/summary
 * - 313 endpoints rendered straight (no virtualization yet); fast enough on
 *   modern browsers. Phase 2 swap to react-virtual if scroll feels janky.
 */
export function Sidebar() {
  const byTag = useEndpointsByTag();
  const panels = usePanels();
  const [filter, setFilter] = useState('');
  const filtered = useMemo(() => filterTagMap(byTag, filter), [byTag, filter]);

  // Desktop: static; mobile: drawer overlay
  return (
    <>
      {/* Backdrop for mobile drawer */}
      {panels.sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
          aria-label="Close sidebar"
        />
      )}
      <aside
        className={cn(
          'w-[280px] shrink-0 flex-col border-r-[3px] border-border-strong bg-base z-40',
          'lg:flex lg:static',
          panels.sidebarOpen
            ? 'fixed inset-y-0 left-0 top-16 flex'
            : 'hidden lg:flex'
        )}
      >
        <div className="px-4 py-4 border-b-2 border-border-strong flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary pointer-events-none" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter endpoints..."
              className="w-full h-10 pl-9 pr-3 rounded-pill border-2 border-border-strong bg-elevated text-sm font-display font-semibold placeholder:font-normal placeholder:text-fg-tertiary focus:outline-none focus:shadow-brutal-sm transition-shadow"
            />
          </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden w-10 h-10 rounded-pill border-2 border-border-strong bg-elevated shadow-brutal-sm flex items-center justify-center"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {[...filtered.entries()].map(([tag, endpoints]) => (
            <TagGroup key={tag} tag={tag} endpoints={endpoints} expandAll={filter.length > 0} />
          ))}
          {filtered.size === 0 && (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">
              No endpoints match "{filter}"
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function filterTagMap(
  map: Map<string, EndpointInfo[]>,
  filter: string
): Map<string, EndpointInfo[]> {
  if (!filter) return map;
  const q = filter.toLowerCase();
  const out = new Map<string, EndpointInfo[]>();
  for (const [tag, eps] of map) {
    const hits = eps.filter(
      (e) =>
        e.path.toLowerCase().includes(q) ||
        e.summary.toLowerCase().includes(q) ||
        e.method.toLowerCase().includes(q) ||
        tag.toLowerCase().includes(q)
    );
    if (hits.length > 0) out.set(tag, hits);
  }
  return out;
}

function TagGroup({
  tag,
  endpoints,
  expandAll,
}: {
  tag: string;
  endpoints: EndpointInfo[];
  expandAll: boolean;
}) {
  const [open, setOpen] = useState(false);
  const expanded = open || expandAll;

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm font-display font-bold text-fg hover:bg-overlay transition-colors"
      >
        <ChevronRight
          size={12}
          className={cn('text-fg-tertiary transition-transform', expanded && 'rotate-90')}
        />
        <span className="flex-1 text-left truncate">{tag}</span>
        <span className="text-2xs font-mono font-bold text-fg-tertiary shrink-0 px-1.5 py-0.5 border-2 border-border-strong rounded-pill bg-elevated">
          {endpoints.length}
        </span>
      </button>
      {expanded && (
        <div className="space-y-1 py-1 px-2">
          {endpoints.map((ep) => (
            <EndpointLink key={ep.operationId} endpoint={ep} />
          ))}
        </div>
      )}
    </div>
  );
}

function EndpointLink({ endpoint }: { endpoint: EndpointInfo }) {
  const match = useMatch({
    from: '/endpoints/$tag/$operation',
    shouldThrow: false,
  });
  const isActive = match?.params?.operation === endpointSlug(endpoint.operationId);

  return (
    <Link
      to="/endpoints/$tag/$operation"
      params={{
        tag: tagSlug(endpoint.tag),
        operation: endpointSlug(endpoint.operationId),
      }}
      onClick={() => setSidebarOpen(false)}
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded-brutal-sm text-2xs transition-all',
        isActive
          ? 'bg-accent border-2 border-border-strong shadow-brutal-sm text-fg font-bold'
          : 'text-fg-secondary hover:bg-overlay border-2 border-transparent hover:border-border-strong'
      )}
    >
      <MethodBadge method={endpoint.method} />
      <span className="font-mono truncate flex-1 text-[11px]">
        {endpoint.path.replace(/^\/api\//, '')}
      </span>
    </Link>
  );
}
